export interface DispatchRun {
  run_id: string;
  ticket_id: number;
  new_title: string | null;
  dispatch_rule: string | null;
  assignee_name: string | null;
  status: string;
  duration_ms: number | null;
  trigger: string;
  board_id: number | null;
  automated_dispatch: boolean | null;
  started_at: string;
  dispatch_error: string | null;
  dispatch_result: string | null;
}

export interface DispatchStep {
  step_id: number;
  run_id: string;
  step_name: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  metrics: Record<string, unknown> | null;
  error: Record<string, unknown> | null;
}

export interface DispatchLogEvent {
  id: number;
  run_id: string;
  step_id: number | null;
  ts: string;
  level: string;
  logger: string | null;
  message: string;
  /** Optional extra payload; may be object or string from API. Display concatenated with message. */
  data?: Record<string, unknown> | string | null;
  exception: Record<string, unknown> | null;
}

/** Format log event data as compact string (for search). */
export function formatLogEventData(data: DispatchLogEvent['data']): string {
  if (data == null) return '';
  if (typeof data === 'string') return data;
  try {
    return JSON.stringify(data);
  } catch {
    return String(data);
  }
}

/** Format log event data as pretty-printed JSON for display. */
export function formatLogDataPretty(data: DispatchLogEvent['data']): string {
  if (data == null) return '';
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data) as unknown;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return data;
    }
  }
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

/** Full display text for a log line (message + data) for search/display. */
export function getLogDisplayText(log: DispatchLogEvent): string {
  const base = log.message || '';
  const dataStr = formatLogEventData(log.data);
  return dataStr ? `${base} ${dataStr}` : base;
}

export interface DispatchRunDetail {
  run: DispatchRun & {
    type?: string | null;
    sub_type?: string | null;
    item?: string | null;
    priority_name?: string | null;
    payload?: Record<string, unknown> | null;
    [key: string]: unknown;
  };
  steps: DispatchStep[];
  logs: DispatchLogEvent[];
}

export interface DispatchRunsResponse {
  total: number;
  runs: DispatchRun[];
  limit: number;
  offset: number;
}

export interface DispatchFiltersState {
  start: string;
  end: string;
  status: string;
  ticket_id: string;
  board_id: string;
  trigger: string;
  automated: boolean | null;
  assignee: string;
  error_only: boolean;
}
